import {
  Box,
  BrandLogo,
  Icon,
  IconButton,
  Link,
  NavPanel,
  Target,
  ThemeOverride
} from "@modernatx-internal/ui-kit-react";
import { usePathname } from "next/navigation";
import React from "react";

import { useParseLocalizedPathname } from "@/hooks/useParseLocalizedPathname";
import { PageHeaderBlockProps } from "@/types/Block";

import { BlockText } from "../BlockText";
import { LocaleSelector } from "../LocaleSelector/LocaleSelector";
import { NavLink } from "../navigation/NavLink";

export const PageHeader: React.FC<PageHeaderBlockProps> = ({
  href,
  links,
  localeSelector,
  logo,
  logoHeight,
  ribbonProps,
  target
}) => {
  const { getLocalizedPathnamesMatch } = useParseLocalizedPathname();
  const pathname = usePathname();
  const id = `c${React.useId().replace(/[^A-Z0-9]/gi, "")}`;
  const [visible, setVisible] = React.useState(false);

  const handleDismiss = React.useCallback(() => setVisible(false), []);
  const handleOpen = React.useCallback(() => setVisible(true), []);
  return (
    <>
      {(ribbonProps || localeSelector) && (
        <ThemeOverride mode="dark">
          <Box
            data-nosnippet="true"
            sx={{
              alignItems: "center",
              backgroundColor: "background01",
              display: [!ribbonProps ? "none" : "flex", null, "flex"],
              px: [5, null, 8]
            }}
          >
            {ribbonProps && (
              <Box sx={{ py: 3, width: "100%" }}>
                <BlockText
                  as="div"
                  display={["none", null, "block"]}
                  text={ribbonProps.labelDesktop}
                />

                <Box
                  sx={{
                    display: ["flex", "flex", "none"],
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <BlockText text={ribbonProps.labelMobile} />
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {ribbonProps.linkMobile && (
                      <Link
                        sx={{ color: "text01" }}
                        target={ribbonProps.linkMobile.external ? "_blank" : undefined}
                        href={ribbonProps.linkMobile.href}
                      >
                        {ribbonProps.linkMobile.title}
                      </Link>
                    )}
                    {/* SPKVX-2891, request found in a comment from Jun 27, 2025 */}
                    {ribbonProps.linkMobile2 && (
                      <Link
                        sx={{
                          color: "text01"
                        }}
                        target={ribbonProps.linkMobile2.external ? "_blank" : undefined}
                        href={ribbonProps.linkMobile2.href}
                      >
                        {ribbonProps.linkMobile2.title}
                      </Link>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
            {localeSelector && (
              <Box
                sx={{
                  display: ["none", null, "flex"],
                  flexGrow: 1,
                  width: "100%",
                  justifyContent: "end",
                  mt: 3,
                  mb: 3
                }}
              >
                <LocaleSelector size="md" {...localeSelector} />
              </Box>
            )}
          </Box>
        </ThemeOverride>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: [5, 5, 5],
          justifyContent: "space-between",
          height: [20, 20, 20]
        }}
        title="Primary Navigation"
        role="navigation"
      >
        {logo ? (
          <Target
            href={href}
            target={target}
            sx={{
              flexShrink: 0,
              marginInlineEnd: 5,
              marginInlineStart: [5, 5, 8]
            }}
          >
            <BrandLogo logo={logo} sx={{ height: logoHeight || 8, width: "auto" }} />
          </Target>
        ) : (
          <Box />
        )}
        <Box
          as="nav"
          sx={{
            display: ["none", "none", "flex"],
            fontSize: 2,
            fontWeight: "bold",
            gap: 10,
            overflowY: "auto",
            scrollbarWidth: "none",
            // @ts-ignore
            "::-webkit-scrollbar": {
              display: "none"
            }
          }}
        >
          {links?.map((link, i) => (
            <Box
              key={i}
              sx={{
                flexShrink: 0,
                paddingInlineEnd: i === links?.length - 1 ? [5, 5, 8] : null
              }}
            >
              <NavLink
                active={getLocalizedPathnamesMatch(link.href, pathname)}
                data-scroll={id}
                icon={link.icon}
                index={i}
                href={link.href}
                fontWeight={"bold"}
              >
                <BlockText
                  as="span"
                  text={link.title}
                  // SPKVX-2891, request found in a comment from Jun 27, 2025. Color change only in nav bar not side panel
                  color={link.title === "Dear Healthcare Provider Letter" ? "accent01" : null}
                />
              </NavLink>
            </Box>
          ))}
        </Box>
        {(links || localeSelector) && (
          <Box sx={{ display: ["flex", "flex", "none"] }}>
            <IconButton
              icon="list"
              variant="transparent"
              aria-label="menu-toggle"
              size="lg"
              onClick={handleOpen}
            />
          </Box>
        )}
      </Box>
      <NavPanel data-nosnippet="true" visible={visible} onDismiss={handleDismiss}>
        <Box
          as="nav"
          sx={{
            display: "flex",
            flexDirection: "column"
          }}
        >
          {localeSelector && <LocaleSelector size="lg" {...localeSelector} />}
          {links?.map((link, i) => (
            <Target
              key={i}
              href={link.href}
              sx={{
                color: "text01",
                display: "flex",
                fontSize: 3,
                fontWeight: "bold",
                gap: 2,
                lineHeight: 3,
                py: 2,
                transition: "colorMedium",
                ":hover": {
                  color: "accent01"
                }
              }}
              onClick={handleDismiss}
            >
              <BlockText as="span" text={link.title} />
              {!!link.icon && <Icon color="inherit" icon={link.icon} />}
            </Target>
          ))}
        </Box>
      </NavPanel>
    </>
  );
};
